import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import * as actions from "../../../store/actions/index";
import { withRouter } from "../../../shared/utility";
import { canManageTenantSettings } from "../../../shared/tenantPermissions";
import { LocationsViewModel } from "../../../viewmodels/setup/locations/LocationsViewModel";
import EmptyModal from "../../../components/modals/EmptyModal";
import AuditActionLink from "../../../components/audit/AuditActionLink";
import { auditHistoriesSetupAction } from "../../../components/audit/auditEntityConfig";
import {
  SetupActions,
  SetupPage,
  SetupTable,
  SetupTd,
} from "../../../components/setup/SetupPage";

function LocationsForbidden() {
  return (
    <div className="min-h-[60vh] bg-[#F6F8FB] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl rounded-[18px] border border-[#E2E8F0] bg-white px-6 py-10 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <h1 className="text-2xl font-semibold text-[#0F172A]">Access denied</h1>
        <p className="mt-3 text-sm leading-6 text-[#64748B]">
          Only company administrators can manage locations.
        </p>
      </div>
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={
        active
          ? "inline-flex rounded-full bg-[#DCFCE7] px-2.5 py-0.5 text-xs font-semibold text-[#166534]"
          : "inline-flex rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-semibold text-[#64748B]"
      }
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function DefaultBadge() {
  return (
    <span className="inline-flex rounded-full bg-[#DBEAFE] px-2.5 py-0.5 text-xs font-semibold text-[#1D4ED8]">
      Default
    </span>
  );
}

function Locations(props) {
  const { logout, user_type, permissions, roles } = props;
  const navigate = useNavigate();
  const canManage = canManageTenantSettings(user_type, permissions, roles);

  const {
    emptyModal,
    setEmptyModelOpen,
    loading,
    locations,
    getLocations,
    askDeleteLocation,
  } = LocationsViewModel(logout, props.addSnackbar);

  const [locationFirst, setLocationFirst] = useState(0);
  const [locationRows, setLocationRows] = useState(20);
  const locationSearchRef = useRef();

  useEffect(() => {
    if (canManage) {
      getLocations("", 1);
    }
  }, [canManage]);

  if (!canManage) {
    return <LocationsForbidden />;
  }

  const handleLocationSearch = () => {
    getLocations(locationSearchRef.current?.value ?? "", 1);
  };

  const handleLocationPagination = (event) => {
    const page = event.page + 1;
    setLocationFirst(event.first);
    setLocationRows(event.rows);
    getLocations(locationSearchRef.current?.value ?? "", page);
  };

  return (
    <div>
      <SetupPage
        title="Locations"
        description="Manage ship-to locations and storage zones used on purchase orders and receiving."
        searchPlaceholder="Search locations"
        searchRef={locationSearchRef}
        onSearch={handleLocationSearch}
        actions={[
          {
            label: "Add Location",
            onClick: () => navigate("/locations/new"),
          },
          auditHistoriesSetupAction("location"),
        ]}
        loading={loading}
        tableColumns={["Location", "Status"]}
        totalRecords={locations.total}
        first={locationFirst}
        rows={locationRows}
        onPageChange={handleLocationPagination}
      >
        <SetupTable
          columns={["Location", "Status", "Default"]}
          emptyText="No locations found."
        >
          {locations.data?.map((item) => (
            <tr key={item.id} className="transition hover:bg-[#F6F8FB]/80">
              <SetupTd muted={false}>
                <Link
                  to={`/locations/${item.id}`}
                  className="font-medium text-[#2563EB] hover:text-[#1D4ED8]"
                >
                  {item.name}
                </Link>
              </SetupTd>
              <SetupTd muted={true}>
                <StatusBadge active={item.is_active !== 0} />
              </SetupTd>
              <SetupTd muted={true}>
                {item.is_default === 1 ? <DefaultBadge /> : "—"}
              </SetupTd>
              <SetupActions>
                <AuditActionLink
                  slug="location"
                  id={item.id}
                  name={item.name}
                />
                <Link
                  to={`/locations/${item.id}`}
                  className="inline-flex min-w-[68px] justify-center rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:border-[#2563EB] hover:bg-[#EFF6FF] hover:text-[#2563EB]"
                >
                  Open
                </Link>
                <button
                  type="button"
                  onClick={() => askDeleteLocation(item)}
                  className="ml-2 inline-flex min-w-[68px] justify-center rounded-lg border border-[#FECACA] bg-white px-3 py-1.5 text-sm font-semibold text-[#DC2626] shadow-sm transition hover:border-[#FCA5A5] hover:bg-[#FEF2F2]"
                >
                  Delete
                </button>
              </SetupActions>
            </tr>
          ))}
        </SetupTable>
      </SetupPage>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user_type: state.auth.user_type,
  permissions: state.auth.permissions,
  roles: state.auth.roles,
});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Locations),
);
